import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { Tab } from "components/Layout/GlobalManagement/ConfigDuplication/Tabs/Tabs";
import { ImportedAchievement } from "types/achievements";
import { CampaignBEData } from "types/campaigns";
import { EventSchemaPayload } from "types/customEvents";
import { RewardFieldsBE } from "types/rewards";
import { BEWalletTypeFormat } from "types/wallets";

type Imported = {
  [key in Tab]?: any[];
};

export interface InitialState {
  isDuplicateIntroMessageSeen: boolean;
  imported: Imported;
}

const initialState: InitialState = {
  isDuplicateIntroMessageSeen: false,
  imported: {},
};

export const globalManagementSlice = createSlice({
  name: "globalManagement",
  initialState,
  reducers: {
    setIsDuplicateIntroMessageSeen: (state, action: PayloadAction<boolean>) => {
      state.isDuplicateIntroMessageSeen = action.payload;
    },
    setImported: (state, action: PayloadAction<Imported>) => {
      state.imported = action.payload;
    },
    updateImportedGlobalWallets: (
      state,
      action: PayloadAction<{
        importedId: number;
        wallet: BEWalletTypeFormat;
      }>,
    ) => {
      state.imported[Tab.WALLET] = state.imported[Tab.WALLET]?.map(
        (wallet, index) => {
          if (index === action.payload.importedId) {
            return {
              ...action.payload.wallet,
            };
          }

          return wallet;
        },
      );
    },
    updateImportedGlobalCustomEvents: (
      state,
      action: PayloadAction<{
        importedId: number;
        customEventSchema: EventSchemaPayload;
        isSaved?: boolean;
      }>,
    ) => {
      state.imported[Tab.CUSTOM_EVENT] = state.imported[Tab.CUSTOM_EVENT]?.map(
        (item, index) => {
          if (index === action.payload.importedId) {
            return {
              isSaved: action.payload.isSaved,
              ...action.payload.customEventSchema,
            };
          }

          return item;
        },
      );
    },
    updateImportedGlobalRewards: (
      state,
      action: PayloadAction<{
        importedId: number;
        reward: RewardFieldsBE;
      }>,
    ) => {
      state.imported[Tab.REWARDS] = state.imported[Tab.REWARDS]?.map(
        (reward, index) => {
          if (index === action.payload.importedId) {
            return action.payload.reward;
          }

          return reward;
        },
      );
    },
    updateImportedGlobalAchievements: (
      state,
      action: PayloadAction<{
        importedId: number;
        achievement: ImportedAchievement;
      }>,
    ) => {
      state.imported[Tab.ACHIEVEMENTS] = state.imported[Tab.ACHIEVEMENTS]?.map(
        (achievement, index) => {
          if (index === action.payload.importedId) {
            return action.payload.achievement;
          }

          return achievement;
        },
      );
    },
    updateImportedGlobalCampaigns: (
      state,
      action: PayloadAction<{
        importedId: number;
        campaign: CampaignBEData;
      }>,
    ) => {
      state.imported[Tab.CAMPAIGNS] = state.imported[Tab.CAMPAIGNS]?.map(
        (campaign, index) => {
          if (index === action.payload.importedId) {
            return action.payload.campaign;
          }

          return campaign;
        },
      );
    },
    updateImportedGlobalReferrals: (
      state,
      action: PayloadAction<{
        importedId: number;
        campaign: CampaignBEData;
      }>,
    ) => {
      state.imported[Tab.REFERRALS] = state.imported[Tab.REFERRALS]?.map(
        (campaign, index) => {
          if (index === action.payload.importedId) {
            return action.payload.campaign;
          }

          return campaign;
        },
      );
    },
    updateImportedGlobalAutomations: (
      state,
      action: PayloadAction<{
        importedId: number;
        campaign: CampaignBEData;
      }>,
    ) => {
      state.imported[Tab.AUTOMATIONS] = state.imported[Tab.AUTOMATIONS]?.map(
        (campaign, index) => {
          if (index === action.payload.importedId) {
            return action.payload.campaign;
          }

          return campaign;
        },
      );
    },
  },
});

export const {
  setIsDuplicateIntroMessageSeen,
  setImported,
  updateImportedGlobalWallets,
  updateImportedGlobalCustomEvents,
  updateImportedGlobalRewards,
  updateImportedGlobalAchievements,
  updateImportedGlobalCampaigns,
  updateImportedGlobalReferrals,
  updateImportedGlobalAutomations,
} = globalManagementSlice.actions;

export const selectIsDuplicateIntroMessageSeen = (state: RootState) =>
  state.globalManagement.isDuplicateIntroMessageSeen;

export const selectImportedResources = (state: RootState) =>
  state.globalManagement.imported;

export default globalManagementSlice.reducer;
